import {
  Bookmark,
  BookmarkBorder,
  Favorite,
  FavoriteBorder,
  LocalFireDepartment,
  FitnessCenter
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Skeleton,
  Typography,
  useMediaQuery,
  Paper,
  Chip
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useTheme } from "@emotion/react";
import getInitials from "../../utils/getInitials";
import { formatDate } from "../../utils/formatDate";
import useSignedUrl from "../../utils/useSignedUrl";
import PreviewModal from "../Modals/PreviewMealModal";
import useThemeColor from "../../utils/useTheme";
import { updateMealsData } from "../../features/meals/mealsSlice";
import { useDispatch } from "react-redux";

const MealCard = ({
  currentTab,
  meal,
  key,
  isFromViewingMeal = false,
  setIsViewingUserMeals,
  onLikeToggle,
  onFavourite,
  setIsRefresh,
  setOpenSnackbar,
  setSnackbarMessage
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  // const imgSrc = useSignedUrl(
  //   meal?.createdByUser?.profilePic || meal?.createdByAdminUser?.profilePic,
  //   process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  // );
  const [openPreview, setOpenPreview] = React.useState(null);
  const [isHovered, setIsHovered] = React.useState(false);
  // const { loading } = useSignedUrl(
  //   meal?.mealImage,
  //   process.env.REACT_APP_BACKEND_MEAL_IMAGE_LOCATION,
  //   true
  // );
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const dispatch = useDispatch();
  const styles = useThemeColor();
  useEffect(() => {
    setIsHovered(false);
  }, [openPreview]);
  const cardHeight = isMobile ? "100%" : "320px";
  const imageHeight = isMobile ? "90%" : "200px";

  const cardWidth = useMemo(() => {
    return isMobile ? windowWidth / 2 - 20 : "250px"; // Adjust breakpoint as needed
  }, [windowWidth]);
  return (
    <Paper
      key={key}
      elevation={isHovered ? 8 : 2}
      sx={{
        width: cardWidth,
        height: cardHeight,
        borderRadius: 2,
        overflow: "hidden",
        background: styles.lightGreyColor,
        transition: "all 0.3s ease",
        transform: isHovered ? "translateY(-8px)" : "none",
        ":hover": {
          cursor: "pointer"
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => {
        setOpenPreview(meal);
      }}
    >
      <Box display="flex" flexDirection="column" height="100%">
        {/* Image Section */}
        <Box position="relative" width="100%" height={imageHeight}>
          {/* {loading ? (
            <Skeleton
              variant="rectangular"
              sx={{ background: "gray" }}
              height="100%"
              width="100%"
            />
          ) : ( */}
          <Box
            component="img"
            loading="lazy"
            src={meal?.mealImage}
            height="100%"
            width="100%"
            sx={{
              objectFit: "cover",
              transition: "transform 0.3s ease",
              transform: isHovered ? "scale(1.05)" : "scale(1)"
            }}
          />
          {/* } */}

          {/* Nutritional info as chips overlaid on image */}
          <Box
            position="absolute"
            bottom="8px"
            left="8px"
            display="flex"
            gap="8px"
          >
            <Chip
              label={`${meal?.total800Gms}`}
              size="small"
              sx={{
                backgroundColor: "#F6541C",
                color: "#fff",
                fontWeight: "bold",
                backdropFilter: "blur(5px)",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
              }}
            />
            <Chip
              label={`${meal?.totalProteinGms || 0}`}
              size="small"
              sx={{
                backgroundColor: "#2C75C5",
                color: "#fff",
                fontWeight: "bold",
                backdropFilter: "blur(5px)",
                boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
              }}
            />
          </Box>
        </Box>

        {/* Content Section */}
        <Box
          display="flex"
          flexDirection="column"
          flex="1"
          p={1.5}
          justifyContent="space-between"
        >
          {/* Title */}
          <Box>
            <Typography
              fontWeight={600}
              fontSize="16px"
              sx={{
                color: styles.color,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                width: "100%"
              }}
            >
              {meal?.title}
            </Typography>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
          >
            <Typography
              color={styles.modLabel}
              fontSize="12px"
              fontWeight={500}
            >
              {formatDate(meal?.scheduledDate)}
            </Typography>

            <Box display="flex" alignItems="center" gap={1}>
              {/* Like Button */}
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  onLikeToggle(meal?._id);
                  dispatch(
                    updateMealsData({
                      likedByUser: !meal?.likedByUser,
                      mealId: meal?._id
                    })
                  );
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  borderRadius: "16px",
                  // backgroundColor: meal?.likedByUser
                  //   ? "rgba(255, 64, 0, 0.1)"
                  //   : "transparent",
                  transition: "all 0.2s ease",
                  padding: "2px",
                  ":hover": {
                    backgroundColor: meal?.likedByUser
                      ? "rgba(255, 64, 0, 0.2)"
                      : "rgba(0, 0, 0, 0.05)"
                  }
                }}
              >
                {meal?.likedByUser ? (
                  <Favorite
                    sx={{
                      height: "18px",
                      width: "18px",
                      fill: "#FF4000"
                    }}
                  />
                ) : (
                  <FavoriteBorder
                    sx={{
                      height: "18px",
                      width: "18px",
                      fill: styles.color
                    }}
                  />
                )}
                <Typography
                  fontWeight={500}
                  color={meal?.likedByUser ? "#FF4000" : styles.color}
                  fontSize={"12px"}
                >
                  {meal?.likeCount}
                </Typography>
              </Box>

              {/* Bookmark Button */}
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  onFavourite(meal?._id);
                  dispatch(
                    updateMealsData({
                      favouriteByUser: !meal?.favouriteByUser,
                      mealId: meal?._id
                    })
                  );
                  // if (currentTab === "favorites") {
                  //   setIsRefresh(true);
                  // }
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "16px",
                  // backgroundColor: meal?.favouriteByUser
                  //   ? "rgba(3, 181, 92, 0.1)"
                  //   : "transparent",
                  transition: "all 0.2s ease",
                  padding: "2px",
                  ":hover": {
                    backgroundColor: meal?.favouriteByUser
                      ? "rgba(3, 181, 92, 0.2)"
                      : "rgba(0, 0, 0, 0.05)"
                  }
                }}
              >
                {meal?.favouriteByUser ? (
                  <Bookmark
                    sx={{
                      height: "18px",
                      width: "18px",
                      fill: "#03B55C"
                    }}
                  />
                ) : (
                  <BookmarkBorder
                    sx={{
                      height: "18px",
                      width: "18px",
                      fill: styles.color
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* User info */}
          {!isFromViewingMeal && (
            <Box
              display="flex"
              gap="8px"
              alignItems="center"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setTimeout(() => {
                  setIsViewingUserMeals(meal);
                }, 1000);
              }}
              sx={{
                padding: "8px",
                borderRadius: "8px",
                backgroundColor: "rgba(0,0,0,0.02)",
                transition: "background-color 0.2s ease",
                ":hover": {
                  backgroundColor: "rgba(0,0,0,0.05)"
                }
              }}
            >
              {meal?.createdByUser?.profilePic ||
              meal?.createdByAdminUser?.profilePic ? (
                <Box
                  component="img"
                  loading="lazy"
                  height={32}
                  borderRadius="50%"
                  width={32}
                  src={
                    meal?.createdByUser?.profilePic ||
                    meal?.createdByAdminUser?.profilePic
                  }
                  alt="avatar"
                />
              ) : (
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%"
                  }}
                >
                  <Typography
                    color={styles.inputLabelColor}
                    fontSize="13px"
                    fontWeight={400}
                  >
                    {getInitials(
                      meal?.createdByAdminUser
                        ? meal?.createdByAdminUser?.name
                        : meal?.createdByUser?.displayName
                    )}
                  </Typography>
                </Avatar>
              )}
              <Typography
                fontSize="13px"
                fontWeight={400}
                color={styles.inputLabelColor}
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap"
                }}
              >
                {meal?.createdByAdminUser
                  ? meal?.createdByAdminUser?.name
                  : meal?.createdByUser?.displayName}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <PreviewModal
        onClose={() => {
          setOpenPreview(null);
        }}
        isViewingUserMeals={isFromViewingMeal}
        currentMeal={openPreview}
        setIsViewingUserMeals={setIsViewingUserMeals}
        setOpenSnackbar={setOpenSnackbar}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Paper>
  );
};

export default MealCard;
